import { Component, OnInit } from '@angular/core';
import { SharedService } from '../../services/shared.service';

@Component({
  selector: 'app-home-widget',
  templateUrl: './home-widget.component.html',
  styleUrls: ['./home-widget.component.scss'],
  host: {
    '(dragstart)': '$event.stopPropagation()',
    '(dragover)': '$event.stopPropagation()',
    '(drop)': '$event.stopPropagation()'
  }
})
export class HomeWidgetComponent implements OnInit {
  widgetList = [];
  isLoading = false;
  filter: string = 'all';
  activeTab: string = 'widgets';

  // Inline form (slide-in panel)
  showForm = false;
  editWidgetId: any = '';

  // Drag and drop
  dragIndex: number = null;
  dragOverIndex: number = null;

  // Share
  shareWidgetList = [];
  usersList = [];
  selectedUsers = [];
  dropdownSettings = {};
  groupList = [];
  groupedWidgetTitle = '';
  groupedWidgetId = '';
  groupedWidgets = [];

  // Settings - Add Group
  newGroupName = '';

  // Settings - New Track form
  settingsEditingId: string = null;
  settingsSelectedGroup = '';
  settingsDataSources = [];
  settingsSelectedSources = [];
  settingsSourceDropdownSettings = {};
  settingsGoal: number = null;
  settingsDataType = '';

  // Grouped view
  groups = [];
  todayFormatted = '';

  // Inline share panel
  sharingGroupId: string = null;
  sharingGroupData: any = null;

  // User permissions
  isAdmin = false;
  userData: any;

  private isLocalhost = false;
  private localStorageKey = 'home_widget_data';

  constructor(private sharedService: SharedService) {}

  ngOnInit() {
    this.isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    // Load user and check permissions
    this.userData = JSON.parse(localStorage.getItem('user'));
    this.isAdmin = this.userData && this.userData.user_level !== 'inspector';

    this.todayFormatted = new Date().toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });

    this.dropdownSettings = {
      singleSelection: false, idField: 'user_id', textField: 'user_full_name',
      selectAllText: 'Select All', unSelectAllText: 'UnSelect All', allowSearchFilter: true
    };

    this.settingsSourceDropdownSettings = {
      singleSelection: false, idField: 'id', textField: 'name',
      selectAllText: 'Select All', unSelectAllText: 'UnSelect All', allowSearchFilter: true
    };

    if (this.isLocalhost) {
      this.loadFromLocalStorage();
    } else {
      this.getWidgets();
      this.getUsersList();
      this.getGroupWidgets();
    }
    this.loadDataSources();
  }

  // === LOCAL STORAGE (localhost persistence) ===
  private loadFromLocalStorage() {
    this.isLoading = false;
    try {
      const saved = localStorage.getItem(this.localStorageKey);
      if (saved) {
        const data = JSON.parse(saved);
        this.groups = data.groups || [];
        this.rebuildWidgetList();
      }
    } catch (e) {}

    this.getUsersList();
  }

  private saveToLocalStorage() {
    if (!this.isLocalhost) return;
    localStorage.setItem(this.localStorageKey, JSON.stringify({ groups: this.groups }));
  }

  private rebuildWidgetList() {
    this.widgetList = [];
    this.groups.forEach(g => {
      g.widgets.forEach(w => this.widgetList.push(w));
    });
  }

  switchTab(tab: string) {
    this.activeTab = tab;
    if (tab === 'settings' && !this.isLocalhost) {
      this.getShareWidgets();
      this.getGroupWidgets();
    }
  }

  // === WIDGET TAB ===
  openForm(id?) {
    this.editWidgetId = id || '';
    this.showForm = true;
  }

  onFormSaved() {
    this.showForm = false;
    this.editWidgetId = '';
    this.getWidgets();
  }

  onFormCancelled() {
    this.showForm = false;
    this.editWidgetId = '';
  }

  discardWidget(id) {
    if (this.isLocalhost) {
      this.groups.forEach(g => {
        g.widgets = g.widgets.filter(w => w.id !== id);
      });
      // Remove empty groups (except keep named ones)
      this.groups = this.groups.filter(g => g.widgets.length > 0 || g.id !== 'UNGROUPED');
      this.rebuildWidgetList();
      this.saveToLocalStorage();
      return;
    }
    this.sharedService.removeWidget(id).subscribe(() => this.getWidgets());
  }

  getWidgets() {
    if (this.isLocalhost) { this.loadFromLocalStorage(); return; }
    this.widgetList = [];
    this.isLoading = true;
    this.sharedService.getWidgets().subscribe(data => this.loadRealWidgets(data));
  }

  private loadRealWidgets(data) {
    this.widgetList = [];
    for (const wid of data) {
      this.sharedService.getWidgetDatas(wid).subscribe(d => {
        this.isLoading = false;
        if (d) {
          this.widgetList.push({
            id: wid.widget_id, title: d.title, result_data: d.data, symbol: d.data_type_symbol,
            goal: d.goal, is_multiple: d.is_multiple, is_null_result: d.is_null_result,
            exceed_goal: d.exceed_goal, percentage_value: d.percentage_value,
            datapercentage_val: d.datapercentage_val, shouldbepercentage_val: d.shouldbepercentage_val,
            shouldbe_color: d.shouldbe_color, displayStartDate: d.displayStartDate,
            displayEndDate: d.displayEndDate, diffrence: d.diffrence, target: d.target,
            shouldbe: d.shouldbe, created: wid.created, order_id: wid.order_id,
            dataSource: ''
          });
          this.widgetList.sort((a, b) => a.order_id - b.order_id);
          this.buildGroupsFromWidgets();
        }
      });
    }
  }

  private buildGroupsFromWidgets() {
    const groupMap = {};
    this.widgetList.forEach(w => {
      const g = w.group || 'UNGROUPED';
      if (!groupMap[g]) groupMap[g] = { name: g, id: g, widgets: [], sharedUsers: [], sharedWidgetIds: [] };
      groupMap[g].widgets.push(w);
    });
    const all = Object.values(groupMap) as any[];
    const ungrouped = all.filter(g => g.id === 'UNGROUPED');
    const rest = all.filter(g => g.id !== 'UNGROUPED');
    this.groups = [...ungrouped, ...rest];
  }

  get filteredGroups() {
    if (this.filter === 'all') return this.groups;
    return this.groups.map(g => ({
      ...g,
      widgets: g.widgets.filter(w =>
        this.filter === 'on-track' ? w.shouldbe_color === 'green' : w.shouldbe_color === 'red'
      )
    })).filter(g => g.widgets.length > 0);
  }

  get onTrackCount(): number {
    return this.widgetList.filter(w => w.shouldbe_color === 'green').length;
  }

  get behindCount(): number {
    return this.widgetList.filter(w => w.shouldbe_color === 'red').length;
  }

  getGroupHealth(group): number {
    if (!group.widgets || group.widgets.length === 0) return 0;
    const onTrack = group.widgets.filter(w => w.shouldbe_color === 'green').length;
    return Math.round((onTrack / group.widgets.length) * 100);
  }

  // === METRIC DRAG (within a group) ===
  dragGroupId: string = null;
  dragMetricIndex: number = null;
  dropMetricIndex: number = null;

  onMetricDragStart(event: DragEvent, group, index: number) {
    event.stopPropagation();
    this.dragGroupId = group.id;
    this.dragMetricIndex = index;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', 'metric');
  }

  onMetricDragOver(event: DragEvent, group, index: number) {
    event.preventDefault();
    event.stopPropagation();
    if (this.dragGroupId === group.id) {
      this.dropMetricIndex = index;
    }
  }

  onMetricDragLeave(event: DragEvent) {
    event.stopPropagation();
    this.dropMetricIndex = null;
  }

  onMetricDrop(event: DragEvent, group, dropIndex: number) {
    event.preventDefault();
    event.stopPropagation();
    if (this.dragGroupId !== group.id || this.dragMetricIndex === null || this.dragMetricIndex === dropIndex) {
      this.resetMetricDrag();
      return;
    }

    const item = group.widgets.splice(this.dragMetricIndex, 1)[0];
    group.widgets.splice(dropIndex, 0, item);

    this.rebuildWidgetList();
    if (this.isLocalhost) {
      this.saveToLocalStorage();
    } else {
      this.sharedService.reOrderWidgets(this.widgetList.map((w, i) => ({ widget_id: w.id, title: w.title, order_id: i }))).subscribe();
    }
    this.resetMetricDrag();
  }

  onMetricDragEnd(event: DragEvent) {
    event.stopPropagation();
    this.resetMetricDrag();
  }

  private resetMetricDrag() {
    this.dragGroupId = null;
    this.dragMetricIndex = null;
    this.dropMetricIndex = null;
  }

  isMetricDragging(group, index: number): boolean {
    return this.dragGroupId === group.id && this.dragMetricIndex === index;
  }

  isMetricDropTarget(group, index: number): boolean {
    return this.dragGroupId === group.id && this.dropMetricIndex === index && this.dragMetricIndex !== index;
  }

  // === GROUP DRAG (reorder groups) ===
  dragGroupIndex: number = null;
  dropGroupIndex: number = null;

  onGroupDragStart(event: DragEvent, index: number) {
    event.stopPropagation();
    this.dragGroupIndex = index;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', 'group');
  }

  onGroupDragOver(event: DragEvent, index: number) {
    event.preventDefault();
    event.stopPropagation();
    if (this.dragGroupIndex !== null && this.dragGroupIndex !== index) {
      this.dropGroupIndex = index;
    }
  }

  onGroupDragEnd(event: DragEvent) {
    event.stopPropagation();
    this.dragGroupIndex = null;
    this.dropGroupIndex = null;
  }

  onGroupDrop(event: DragEvent, dropIndex: number) {
    event.preventDefault();
    event.stopPropagation();
    if (this.dragGroupIndex === null || this.dragGroupIndex === dropIndex) {
      this.dragGroupIndex = null;
      this.dropGroupIndex = null;
      return;
    }

    const item = this.groups.splice(this.dragGroupIndex, 1)[0];
    this.groups.splice(dropIndex, 0, item);

    this.rebuildWidgetList();
    if (this.isLocalhost) this.saveToLocalStorage();
    this.dragGroupIndex = null;
    this.dropGroupIndex = null;
  }

  isGroupDragging(index: number): boolean {
    return this.dragGroupIndex === index;
  }

  isGroupDropTarget(index: number): boolean {
    return this.dropGroupIndex === index && this.dragGroupIndex !== index;
  }

  // === SHARE / GROUPS ===
  getShareWidgets() {
    this.sharedService.getWidgets().subscribe(data => { this.shareWidgetList = data; }, () => {});
  }

  getUsersList() {
    this.sharedService.getUsersList().subscribe(data => { this.usersList = data; }, () => {});
  }

  getGroupWidgets() {
    this.sharedService.getGroupedWidget().subscribe(data => { this.groupList = data; }, () => {});
  }

  onSelectAll(items: any) {}

  // Inline share panel
  openShareForGroup(group) {
    this.sharingGroupId = group.id;
    this.sharingGroupData = group;
    // Load existing shared data
    this.selectedUsers = group.sharedUsers ? [...group.sharedUsers] : [];
    this.groupedWidgets = group.sharedWidgetIds ? [...group.sharedWidgetIds] : [];
    this.groupedWidgetTitle = group.name;
  }

  closeSharePanel() {
    this.sharingGroupId = null;
    this.sharingGroupData = null;
    this.selectedUsers = [];
    this.groupedWidgets = [];
  }

  toggleShareWidget(event, widgetId) {
    if (event.target.checked) this.groupedWidgets.push(widgetId);
    else this.groupedWidgets = this.groupedWidgets.filter(id => id !== widgetId);
  }

  saveShareGroup(group) {
    // Save shared users and widget selections back to group
    group.sharedUsers = [...this.selectedUsers];
    group.sharedWidgetIds = [...this.groupedWidgets];

    if (this.isLocalhost) {
      this.saveToLocalStorage();
      this.closeSharePanel();
      return;
    }

    const param = {
      groupId: group.apiGroupId || '',
      groupTitle: group.name,
      groupUsers: this.selectedUsers,
      groupedWidgets: this.groupedWidgets
    };
    this.sharedService.postGroupedWidget(param).subscribe(() => {
      this.closeSharePanel();
      this.getGroupWidgets();
    });
  }

  // === SETTINGS TAB ===
  loadDataSources() {
    this.sharedService.getdataSources().subscribe(data => { this.settingsDataSources = data; }, () => {});
  }

  handleAddGroup() {
    const name = this.newGroupName.trim().toUpperCase();
    if (!name) return;

    if (this.isLocalhost) {
      // Check if group already exists
      if (this.groups.find(g => g.name === name)) { this.newGroupName = ''; return; }
      this.groups.push({ name, id: 'group-' + Date.now(), widgets: [], sharedUsers: [], sharedWidgetIds: [] });
      this.newGroupName = '';
      this.saveToLocalStorage();
      return;
    }

    this.sharedService.postGroupedWidget({ groupId: '', groupTitle: name, groupUsers: [], groupedWidgets: [] }).subscribe(() => {
      this.newGroupName = '';
      this.getGroupWidgets();
    });
  }

  handleDeleteGroup(groupId) {
    if (this.isLocalhost) {
      // Move widgets to UNGROUPED before deleting the group
      const group = this.groups.find(g => g.id === groupId);
      if (group && group.widgets.length > 0) {
        let ungrouped = this.groups.find(g => g.id === 'UNGROUPED');
        if (!ungrouped) {
          ungrouped = { name: 'UNGROUPED', id: 'UNGROUPED', widgets: [], sharedUsers: [], sharedWidgetIds: [] };
          this.groups.unshift(ungrouped);
        }
        group.widgets.forEach(w => { w.group = 'UNGROUPED'; ungrouped.widgets.push(w); });
      }
      this.groups = this.groups.filter(g => g.id !== groupId);
      // Remove empty UNGROUPED
      this.groups = this.groups.filter(g => g.id !== 'UNGROUPED' || g.widgets.length > 0);
      this.rebuildWidgetList();
      this.saveToLocalStorage();
      return;
    }
    this.sharedService.postDeleteGroupWidget({ id: groupId }).subscribe(() => this.getGroupWidgets());
  }

  settingsStartEdit(widget) {
    this.settingsEditingId = widget.id;
    this.settingsGoal = widget.goal;
    this.settingsDataType = widget.symbol === '$' ? 'cash' : '';
    // Find the group this widget belongs to
    const group = this.groups.find(g => g.widgets.some(w => w.id === widget.id));
    if (group) this.settingsSelectedGroup = group.name;
  }

  settingsResetForm() {
    this.settingsEditingId = null;
    this.settingsSelectedGroup = '';
    this.settingsSelectedSources = [];
    this.settingsGoal = null;
    this.settingsDataType = '';
  }

  settingsSaveMetric() {
    if (!this.settingsSelectedGroup && !this.settingsEditingId) return;

    if (this.isLocalhost) {
      if (this.settingsEditingId) {
        // Edit: update widget in its group
        this.groups.forEach(g => {
          const w = g.widgets.find(w => w.id === this.settingsEditingId);
          if (w) {
            w.goal = this.settingsGoal;
            w.symbol = this.settingsDataType === 'cash' ? '$' : '';
            if (this.settingsSelectedSources.length > 0) {
              w.title = this.settingsSelectedSources.map(s => s.name).join(', ');
              w.dataSource = w.title;
            }
          }
        });
      } else {
        // Create new widget in selected group
        const title = this.settingsSelectedSources.length > 0 ? this.settingsSelectedSources.map(s => s.name).join(', ') : 'New Metric';
        const goal = this.settingsGoal || 1000;
        const newWidget = {
          id: Date.now(),
          title: title,
          result_data: 0, symbol: this.settingsDataType === 'cash' ? '$' : '',
          goal: goal, is_multiple: 1, is_null_result: 0, exceed_goal: 0,
          percentage_value: 0, datapercentage_val: '0 100', shouldbepercentage_val: '50 50',
          shouldbe_color: 'red', shouldbe: 0, diffrence: 0, target: 0,
          displayStartDate: new Date().toLocaleDateString('en-US'),
          displayEndDate: new Date().toLocaleDateString('en-US'),
          created: 'yes',
          order_id: this.widgetList.length, group: this.settingsSelectedGroup,
          dataSource: this.settingsSelectedSources.length > 0 ? this.settingsSelectedSources.map(s => s.name).join(', ') : ''
        };

        let targetGroup = this.groups.find(g => g.name === this.settingsSelectedGroup);
        if (!targetGroup) {
          targetGroup = { name: this.settingsSelectedGroup, id: 'group-' + Date.now(), widgets: [], sharedUsers: [], sharedWidgetIds: [] };
          this.groups.push(targetGroup);
        }
        targetGroup.widgets.push(newWidget);
      }

      this.rebuildWidgetList();
      this.saveToLocalStorage();
      this.settingsResetForm();
      return;
    }

    // Production: call API
    const widgetData = {
      id: this.settingsEditingId || null,
      text: this.settingsSelectedSources.length > 0 ? this.settingsSelectedSources[0].name : 'New Metric',
      data_source_id: this.settingsSelectedSources,
      branch_id: 0, agent_id: 0,
      goal: this.settingsGoal || 1000,
      data_type: this.settingsDataType,
      is_today: '1', from_date: null, to_date: null, goal_completed: null
    };

    this.sharedService.addWidget(widgetData).subscribe(
      () => { this.getWidgets(); this.settingsResetForm(); },
      () => { this.settingsResetForm(); }
    );
  }

  settingsUpdateValue(widget, newValue) {
    widget.result_data = parseFloat(newValue) || 0;
    if (this.isLocalhost) this.saveToLocalStorage();
  }

  settingsDeleteMetric(widget) {
    this.discardWidget(widget.id);
  }

  getGroupNames(): string[] {
    return this.groups.map(g => g.name);
  }
}
